<template>
  <div class="edit-form-list-item">
    <div class="history-item">
      <div class="history-item-button">
        <div class="button">
          <el-button
            :disabled="!(index > 0 && length > 1)"
            icon="el-icon-arrow-up"
            circle
            @click.stop="handleCommand('moveUp')"
          ></el-button>
        </div>
        <div class="button">
          <el-button
            :disabled="!(index + 1 != length && length > 1)"
            icon="el-icon-arrow-down"
            circle
            @click.stop="handleCommand('moveDown')"
          ></el-button>
        </div>
      </div>
      <div class="history-item-content">
        <el-form
          class="history-form"
          ref="historyItemForm"
          :model="editableItem"
          :rules="rules"
          @submit.native.prevent
        >
          <el-form-item prop="title" class="edit-form-item">
            <div class="item-name"><label for="title">タイトル</label></div>
            <el-input
              class="input-lg"
              type="textbox"
              id="title"
              :autosize="{ minRows: 1, maxRows: 4 }"
              :placeholder="'アピールしたいプロダクト等のタイトルを入力'"
              v-model="editableItem.title"
              ref="desc"
            ></el-input>
          </el-form-item>

          <el-form-item prop="tools" class="edit-form-item">
            <div class="item-name">
              <label for="tools">使用ツール・言語等</label>
            </div>
            <el-input
              class="input-lg"
              type="textbox"
              id="tools"
              :autosize="{ minRows: 1, maxRows: 4 }"
              :placeholder="'使用したツールや言語等を入力'"
              v-model="editableItem.tools"
              ref="desc"
            ></el-input>
          </el-form-item>

          <el-form-item prop="description" class="edit-form-item">
            <div class="item-name"><label for="description">説明文</label></div>
            <el-input
              type="textarea"
              id="description"
              :autosize="{ minRows: 3, maxRows: 4 }"
              :placeholder="'説明を入力'"
              v-model="editableItem.description"
              ref="desc"
            ></el-input>
          </el-form-item>
          <br /><br />

          <ShokurekishoImageUploadFields :item="editableItem" />
        </el-form>
      </div>
    </div>

    <div class="projects" :class="{ project__invisible: !isActive }">
      <div class="add-project-item enter">
        <el-button icon="el-icon-delete" @click.stop="handleCommand('delete')"
          >削除</el-button
        >
        <el-button icon="el-icon-check" @click.stop="deactivateImageItem"
          >OK</el-button
        >
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeMount, ref } from 'vue'
import { emitter } from '@/plugins/emitter'
import { useHostServices } from '@/composables/format-edit/useHostServices'
import { useHistoryItemDescResize } from '@/composables/format-edit/useShokurekishoHistoryItem'
import type { ShokurekishoImageItem } from '@/composables/format-edit/useShokurekishoFormatData'
import ShokurekishoImageUploadFields from '@/components/format-edit/shokurekisho/ShokurekishoImageUploadFields.vue'

type FormLibWithCharCount = {
  charCount: (value: unknown) => number
}

type ElFormLike = {
  validate: () => Promise<unknown>
}

const props = defineProps<{
  item: ShokurekishoImageItem
  index: number
  length: number
  descriptionPlaceholder?: string
  descriptionMaxLength?: number
  minYear?: number | string
  maxYear?: number | string
  activeImageItem?: string
}>()

const emit = defineEmits<{
  (e: 'add-history-item', index: number, item?: ShokurekishoImageItem): void
  (e: 'delete-history-item', index: number): void
  (e: 'move-history-item', params: { index: number; to: string }): void
}>()

const { formLib } = useHostServices()
const formLibChars = formLib as typeof formLib & FormLibWithCharCount
const historyItemForm = ref<ElFormLike | null>(null)
const { desc } = useHistoryItemDescResize()

const editableItem = computed(() => props.item)
const isActive = computed(() => props.activeImageItem === props.item.uuid)

function charTooLong(value: unknown, max: number) {
  return formLibChars.charCount(value) > max
}

const rules = {
  description: [
    {
      validator: (
        _rule: unknown,
        value: unknown,
        callback: (err?: Error) => void
      ) => {
        if (charTooLong(value, props.descriptionMaxLength || 0)) {
          callback(new Error('Please input digits'))
        } else callback()
      },
      message:
        '一行におさまる文字数をオーバーしています。文章を短くするか、行を ＋ で追加して入力してください。',
      trigger: ['change', 'blur']
    }
  ]
}

function handleCommand(command: string) {
  if (command === 'addAbove') emit('add-history-item', props.index)
  else if (command === 'addBelow') emit('add-history-item', props.index + 1)
  else if (command === 'delete') emit('delete-history-item', props.index)
  else if (command === 'duplicate') {
    emit('add-history-item', props.index, props.item)
  } else if (command === 'moveUp') {
    emit('move-history-item', { index: props.index, to: 'up' })
  } else if (command === 'moveDown') {
    emit('move-history-item', { index: props.index, to: 'down' })
  }
}

function deactivateImageItem() {
  emitter.emit('deactivate-image-item')
}

onBeforeMount(() => {
  nextTick(() => {
    if (!historyItemForm.value) return
    historyItemForm.value
      .validate()
      .then()
      .catch(() => {})
  })
})
</script>

<style lang="scss" scoped>
$white: #fff;
$errorcolor: #f56c6c;
$light-gray: #dcdfe6;

.project__invisible {
  display: none;
}

.history-item {
  display: flex;
  flex-direction: row-reverse;
}

.history-item-content {
  flex: 1;
  margin: 0.5rem 0;
  padding: 0.5rem 1rem;

  .attention-limit {
    font-size: 0.75rem;
    color: $errorcolor;

    &.character {
      padding: 0.5rem 0;
    }

    &.column {
      background: $white;
      margin: 0.5rem 0;
      padding: 0.5rem 1rem;
    }
  }
}

.history-item-button {
  .button {
    display: flex;
  }
}

.projects {
  margin-top: 0.5rem;
}

.add-project-item {
  border-top: dashed 1px $light-gray;
  padding: 1rem;
  text-align: right;
}
</style>
